"use client";

import React from "react";
import { Building2, House as Home, ShieldCheck, ArrowRight, CircleCheck as CheckCircle2, Umbrella, ShieldAlert, Hammer, Check, PhoneCall, ChevronRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
export default function ClaphamServices() {
  const shouldReduceMotion = useReducedMotion();
  const residentialFeatures = ["Roof repairs, pitched tile renewals, and solar installations", "Rear house extensions and loft conversion access towers", "Chimney stack repairs, repointing, and pot replacements", "External wall rendering, brickwork restoration, and painting", "Careful pavement license management for terraced properties"];
  const commercialFeatures = ["Town-centre commercial properties and high-street storefronts", "Multi-storey office developments and modern refurbishments", "Occupied buildings with public protection gantries and fans", "Full RAMS, design calculations, and weekly TG20 inspections", "Dedicated coordination for main contractors and developers"];
  const roofingAccessItems = [{
    title: "Roofing Scaffolding",
    description: "Full perimeter handrails, loading bays, and multi-level work platforms for roofers.",
    to: "/RoofingScaffoldingPage"
  }, {
    title: "Temporary Roofs",
    description: "Weatherproof corrugated and shrink-wrap canopies protecting open roof structures.",
    to: "/TemporaryRoofsPage"
  }, {
    title: "Edge Protection",
    description: "Compliant guardrails, toe boards, and debris netting ensuring height safety on site.",
    to: "/EdgeProtectionPage"
  }];
  const coverageHighlights = ["Town-centre commercial properties", "Office developments & fit-outs", "Residential Victorian terraces & estates", "Refurbishment & mixed-use buildings", "Complex roof and chimney works"];
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="claphamscaffoldingservices" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 font-semibold text-xs tracking-wide">
            Clapham & South West London Coverage
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
            Scaffolding services in Clapham
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed font-default">
            Safe, fully compliant access solutions for town-centre commercial properties, office developments, residential streets, and specialist roofing projects across Clapham and surrounding boroughs.
          </p>

          {/* Quick accreditations & coverage pill */}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground bg-accent px-3 py-1.5 rounded-full">
              <ShieldCheck className="size-4 text-primary" /> NASC Compliant
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground bg-accent px-3 py-1.5 rounded-full">
              <ShieldCheck className="size-4 text-primary" /> CHAS Accredited
            </span>
          </div>
        </div>

        {/* Coverage Scope Strip */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={cardVariants} className="mb-14 rounded-xl border border-border bg-card p-6 md:p-8 shadow-xs">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
            <div className="md:col-span-2">
              <h3 className="text-lg font-semibold text-card-foreground">
                Local project expertise
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Delivering tailored tube-and-fitting and system access across varied urban architectures.
              </p>
            </div>
            <div className="md:col-span-3 flex flex-wrap gap-2">
              {coverageHighlights.map((item, i) => <div key={i} data-index={i} className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-muted text-muted-foreground px-3 py-1.5 rounded-md">
                  <Check className="size-3.5 text-primary shrink-0" />
                  <span>{item}</span>
                </div>)}
            </div>
          </div>
        </motion.div>

        {/* Two Core Subsections: Residential & Commercial */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-14">
          {/* Subsection 1: Residential scaffolding in Clapham */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={cardVariants} className="flex flex-col h-full">
            <Card className="flex flex-col justify-between h-full border border-border shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200">
              <CardHeader className="space-y-3">
                <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <Home className="size-6" />
                </div>
                <div>
                  <CardTitle className="text-2xl font-bold text-card-foreground">
                    Residential scaffolding in Clapham
                  </CardTitle>
                  <CardDescription className="text-base mt-2 text-muted-foreground">
                    Safe, neat access designed for homeowners, builders, and trades on private houses, terraced properties, and residential blocks.
                  </CardDescription>
                </div>
              </CardHeader>

              <CardContent className="space-y-4 pt-2">
                <p className="text-sm text-muted-foreground">
                  Our domestic scaffolding configurations support everything from structural roof repairs and chimney works to exterior rendering and repointing, ensuring safe access while protecting your property.
                </p>

                <div className="space-y-2.5 pt-2">
                  {residentialFeatures.map((feature, i) => <div key={i} data-index={i} className="flex items-start gap-2.5 text-sm text-card-foreground">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </div>)}
                </div>
              </CardContent>

              <CardFooter className="pt-6 border-t border-border flex flex-col sm:flex-row gap-3">
                <Button variant="default" className="w-full sm:w-auto" asChild>
                  <Link to="/ResidentialScaffoldingPage">
                    Residential Scaffolding
                    <ArrowRight className="size-4 ml-1.5" />
                  </Link>
                </Button>
                <Button variant="outline" className="w-full sm:w-auto" asChild>
                  <Link to="/ChimneyScaffoldingPage">
                    Chimney Scaffolding
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>

          {/* Subsection 2: Commercial scaffolding in Clapham */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={cardVariants} className="flex flex-col h-full">
            <Card className="flex flex-col justify-between h-full border border-border shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200">
              <CardHeader className="space-y-3">
                <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <Building2 className="size-6" />
                </div>
                <div>
                  <CardTitle className="text-2xl font-bold text-card-foreground">
                    Commercial scaffolding in Clapham
                  </CardTitle>
                  <CardDescription className="text-base mt-2 text-muted-foreground">
                    Engineered access and perimeter scaffolding for contractors, commercial developers, and busy town-centre sites.
                  </CardDescription>
                </div>
              </CardHeader>

              <CardContent className="space-y-4 pt-2">
                <p className="text-sm text-muted-foreground">
                  We work alongside principal contractors and facilities managers on occupied office developments, retail units, and mixed-use commercial refurbishments, managing pedestrian walkways and full health & safety documentation.
                </p>

                <div className="space-y-2.5 pt-2">
                  {commercialFeatures.map((feature, i) => <div key={i} data-index={i} className="flex items-start gap-2.5 text-sm text-card-foreground">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </div>)}
                </div>
              </CardContent>

              <CardFooter className="pt-6 border-t border-border flex flex-col sm:flex-row gap-3">
                <Button variant="default" className="w-full sm:w-auto" asChild>
                  <Link to="/CommercialScaffoldingPage">
                    Commercial Scaffolding
                    <ArrowRight className="size-4 ml-1.5" />
                  </Link>
                </Button>
                <Button variant="outline" className="w-full sm:w-auto" asChild>
                  <Link to="/MainContractorsDevelopersPage">
                    Main Contractors & Developers
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>
        </div>

        {/* Roofing & Trade Access Block */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={cardVariants} className="rounded-2xl border border-border bg-card p-6 sm:p-8 md:p-10 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 pb-6 border-b border-border gap-4">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider mb-2">
                <Hammer className="size-4" /> Dedicated Trade Solutions
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-card-foreground font-default">
                Roofing & trade access
              </h3>
              <p className="mt-2 text-muted-foreground max-w-2xl text-sm sm:text-base">
                Robust, compliant temporary works for roofing specialists, solar contractors, and general builders requiring secure edge safety and complete weather protection.
              </p>
            </div>
            <Button variant="outline" className="shrink-0" asChild>
              <Link to="/RoofingContractorsPage">
                View Roofing Contractor Services
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {roofingAccessItems.map((item, i) => <div key={i} data-index={i} className="group relative flex flex-col justify-between p-5 rounded-xl bg-accent/40 border border-border/80 hover:border-primary/40 hover:bg-accent/70 transition-[background-color,border-color] duration-200">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="size-9 rounded-md bg-background border border-border flex items-center justify-center text-primary">
                      {i === 0 && <Hammer className="size-4" />}
                      {i === 1 && <Umbrella className="size-4" />}
                      {i === 2 && <ShieldAlert className="size-4" />}
                    </div>
                    <ChevronRight className="size-4 text-muted-foreground group-hover:text-primary transition-colors duration-200" />
                  </div>
                  <h4 className="text-base font-semibold text-card-foreground group-hover:text-primary transition-colors duration-200">
                    {item.title}
                  </h4>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-2 leading-relaxed">
                    {item.description}
                  </p>
                </div>

                <div className="mt-5 pt-3 border-t border-border/50">
                  <Link to={item.to} className="inline-flex items-center text-xs font-semibold text-primary hover:underline underline-offset-4">
                    Explore {item.title}
                    <ArrowRight className="size-3 ml-1" />
                  </Link>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Bottom CTA Strip */}
        <div className="mt-12 rounded-xl bg-secondary text-secondary-foreground p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="text-center sm:text-left">
            <h4 className="text-lg sm:text-xl font-bold">
              Planning a scaffolding project in Clapham?
            </h4>
            <p className="text-sm text-secondary-foreground/80 mt-1">
              Contact Pete Wood and the BMF team for site surveys, TG20 designs, and free fixed-price quotes.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Button variant="default" size="lg" asChild>
              <Link to="/ContactRequestQuotePage">
                Request a Quote
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}